import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import OfferFilterDropdownBox from '../OfferFilterDropdownBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { countOccupants, formatOccupantsForSelectionBox } from 'lib/offer/occupancyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import styled from 'styled-components'

const SelectBoxWithTopBanner = styled.div`
  position: relative;
  width: 100%;
`

const SelectBoxTopBanner = styled.div`
  position: absolute;
  bottom: calc(100% - 1px);
  inset-inline: 0;
  z-index: 1;

  > * {
    border-start-start-radius: ${props => props.theme.borderRadius.S};
    border-start-end-radius: ${props => props.theme.borderRadius.S};
    box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const AttachedOfferFilterDropdownBox = styled(OfferFilterDropdownBox)`
  border-end-start-radius: ${props => props.theme.borderRadius.S};
  border-end-end-radius: ${props => props.theme.borderRadius.S};
`

interface Props {
  rooms?: Array<App.Occupants>
  open: boolean
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  inSidebar?: boolean
  occupantUnit?: 'guest' | 'traveller'
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  multiRoomDisabled?: boolean
  iconDisabled?: boolean
  className?: string
  ref?: React.Ref<HTMLButtonElement>
  topBanner?: React.ReactNode
}

function OfferCapacityWithAgesSelectBox(props: Props) {
  const {
    open,
    rooms = EmptyArray,
    offer,
    inSidebar,
    occupantUnit = 'guest',
    onClick = noop,
    multiRoomDisabled,
    iconDisabled,
    className,
    ref,
    topBanner,
  } = props
  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const allRooms = useMemo(() => countOccupants(rooms), [rooms])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const hasRooms = hasSelectedRooms(rooms)

  const onBoxClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    if (open) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('OCCUPANCY_FILTERS_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
      trackEvent('OCCUPANCY_FILTERS_PRESSED')
      onClick(e)
    }
  }, [open, onClick, dispatch, trackEvent])

  const showInvalid = useMemo(() => !open && allRooms.childrenAge?.some(age => age === -1), [allRooms, open])
  const roomText = useMemo(() => {
    if (multiRoomDisabled) return undefined
    return intl.formatMessage(
      {
        id: 'pM5buVrOIkScBbqPKR_Q',
        defaultMessage: '({saleUnitCount})',
        description: 'Parenthesised pluralised sale unit count in the capacity selection box',
      },
      { saleUnitCount: formatSaleUnitCount(intl, offer.saleUnit, rooms.length) },
    )
  }, [intl, multiRoomDisabled, offer.saleUnit, rooms.length])

  const value = useMemo(() => {
    if (hasRooms) {
      const occupantsText = formatOccupantsForSelectionBox(isTourV2Offer(offer) && isSalesforceTourV2(offer), allRooms)
      return <><b>{occupantsText}</b>{roomText ? ` ${roomText}` : ''}</>
    }
  }, [allRooms, offer, hasRooms, roomText])

  const placeholderText = occupantUnit === 'traveller'
    ? intl.formatMessage({
        id: 'u8OaAgS716N6UudwdETu',
        defaultMessage: 'Select travellers',
        description: 'Placeholder text for traveller selection dropdown on the form',
      })
    : intl.formatMessage({
        id: 'ZOF9Ob0i8n0UCeKC2PiA',
        defaultMessage: 'Select guests',
        description: 'Placeholder text for guest selection dropdown on the offer detail page',
      })

  const DropdownBox = topBanner ? AttachedOfferFilterDropdownBox : OfferFilterDropdownBox
  const dropdownBox = (
    <DropdownBox
      className={topBanner ? undefined : className}
      onClick={onBoxClick}
      active={open}
      ref={ref}
      invalid={showInvalid}
      invalidMessage={intl.formatMessage({
        id: 'se2qQS-6XjuRYwasuPW5',
        defaultMessage: 'Please select children’s ages',
        description: 'Validation error message when children ages are not selected',
      })}
      data-testid="OfferCapacitySelectBox"
      icon={!iconDisabled && <LineUserIcon />}
      inSidebar={inSidebar}
      label={intl.formatMessage({
        id: '6o41-ceqxsIGrDNaN_eD',
        defaultMessage: 'Guests',
        description: 'Label for occupancy selection dropdown in the occupancy or guest picker',
      })}
      placeholder={placeholderText}
      value={value}
    />
  )

  if (topBanner) {
    return (
      <SelectBoxWithTopBanner className={className}>
        <SelectBoxTopBanner>{topBanner}</SelectBoxTopBanner>
        {dropdownBox}
      </SelectBoxWithTopBanner>
    )
  }

  return dropdownBox
}

export default withOfferPageState(state => ({
  rooms: state.rooms,
  open: state.activeFilter === 'capacity',
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
}), OfferCapacityWithAgesSelectBox)
